import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * Store для управления тарифом пользователя (уровень 1: глобальный)
 * Загружается при авторизации и хранит лимиты и доступные фичи
 */
export const useLicenseStore = defineStore('license', () => {
  // === State ===
  const tariff = ref(null)
  const userRoles = ref([]) // Роли пользователя в пространствах
  const isLoading = ref(false)
  const loadedAt = ref(null)
  const TTL = 5 * 60 * 1000 // 5 минут кэш

  // === Getters ===

  // Тариф
  const limits = computed(() => tariff.value?.params?.limits || {})
  const templates = computed(() => tariff.value?.params?.templates || {})
  const tag = computed(() => tariff.value?.tag || 'freemium')
  const title = computed(() => tariff.value?.title || 'Freemium')

  // Роли пользователя
  const roles = computed(() => userRoles.value)
  const roleTitles = computed(() => {
    return Array.isArray(userRoles.value) ? userRoles.value.map(role => role.title) : []
  })
  const spaces = computed(() => {
    const spacesMap = {}
    if (!Array.isArray(userRoles.value)) return {}

    userRoles.value.forEach(role => {
      if (role.company_id && role.title) {
        spacesMap[role.company_id] = role.title
      }
    })
    return spacesMap
  })

  // Проверка лимитов
  const canCreateMoreProjects = computed(() => {
    const limit = limits.value.projects_count?.value
    return limit === -1 || limit === undefined // -1 = безлимит
  })

  const canCreateMoreSpaces = computed(() => {
    const limit = limits.value.spaces_count?.value
    return limit === -1 || limit === undefined
  })

  const maxFileSize = computed(() => limits.value.max_file_mb?.value || 0)
  const storageLimit = computed(() => limits.value.storage_gb?.value || 0)
  const teamLimit = computed(() => limits.value.team_limit?.value || 0)

  /**
   * Проверка доступности фичи
   * Обрабатывает boolean и enum (full/limited/none)
   */
  const hasFeature = (featureName) => {
    const feature = templates.value[featureName]
    if (!feature) return false

    if (feature.type === 'boolean') {
      return feature.value === true || feature.value === 'true'
    }

    if (feature.type === 'enum') {
      if (feature.value === 'full') return true
      if (feature.value === 'none') return false
      if (feature.value === 'limited') return true // limited = доступна
      return feature.value !== 'none' && feature.value !== 'false'
    }

    // Fallback
    const value = feature.value
    if (typeof value === 'boolean') return value
    if (typeof value === 'string') return value !== 'false' && value !== 'none'
    return Boolean(value)
  }

  /**
   * Получить raw значение фичи
   */
  const getFeatureValue = (featureName) => {
    const feature = templates.value[featureName]
    if (!feature) return null
    return feature.value ?? null
  }

  /**
   * Получить полный объект фичи
   */
  const getFeatureObject = (featureName) => {
    return templates.value[featureName] || null
  }

  // Проверка актуальности кэша
  const isStale = computed(() => {
    if (!loadedAt.value) return true
    return Date.now() - loadedAt.value > TTL
  })

  // === Actions ===

  /**
   * Загружает тариф и роли пользователя (при авторизации)
   * @param {object} data - { tariff, roles }
   */
  async function load(data) {
    isLoading.value = true
    try {
      if (data?.tariff) {
        setTariff(data.tariff)
      }
      if (data?.roles) {
        setRoles(data.roles)
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Устанавливает тариф (внутренний метод)
   */
  function setTariff(data) {
    tariff.value = data
    loadedAt.value = Date.now()
  }

  /**
   * Устанавливает роли пользователя (внутренний метод)
   */
  function setRoles(data) {
    userRoles.value = Array.isArray(data) ? data : []
    loadedAt.value = Date.now()
  }

  /**
   * Частичное обновление тарифа (мерж, а не замена)
   */
  function updatePartial(updates) {
    if (!tariff.value) {
      tariff.value = updates
      loadedAt.value = Date.now()
      return
    }

    // Глубокое слияние
    tariff.value = deepMerge(tariff.value, updates)
    loadedAt.value = Date.now()
  }

  /**
   * Очистка store (при logout)
   */
  function clear() {
    tariff.value = null
    userRoles.value = []
    loadedAt.value = null
    isLoading.value = false
  }

  return {
    // State
    tariff,
    userRoles,
    isLoading,
    loadedAt,

    // Getters - Тариф
    limits,
    templates,
    tag,
    title,
    canCreateMoreProjects,
    canCreateMoreSpaces,
    maxFileSize,
    storageLimit,
    teamLimit,
    hasFeature,
    getFeatureValue,
    getFeatureObject,
    isStale,

    // Getters - Роли
    roles,
    roleTitles,
    spaces,

    // Actions
    load,
    setTariff,
    setRoles,
    updatePartial,
    clear
  }
})

// === Helpers ===

/**
 * Глубокое слияние объектов
 */
function deepMerge(target, source) {
  const output = { ...target }

  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (target[key] && typeof target[key] === 'object') {
        output[key] = deepMerge(target[key], source[key])
      } else {
        output[key] = source[key]
      }
    } else {
      output[key] = source[key]
    }
  }

  return output
}
